import { useState } from "react";
import { addMonths, eachDayOfInterval, endOfMonth, endOfWeek, format, isSameMonth, isToday, parseISO, startOfMonth, startOfWeek } from "date-fns";
import { AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react";

const mins = (t) => {
  const [h, m] = (t || "0:0").split(":").map(Number);
  return h * 60 + (m || 0);
};
const endTime = (r) => {
  const e = mins(r.start_time) + r.hours * 60;
  return `${String(Math.floor(e / 60) % 24).padStart(2, "0")}:${String(e % 60).padStart(2, "0")}${e >= 1440 ? " (+1d)" : ""}`;
};

// Approved sessions on the same date whose time ranges overlap with r
export const studioClashes = (r, rows) =>
  rows.filter((o) => o.id !== r.id && o.status === "approved" && o.date === r.date &&
    mins(o.start_time) < mins(r.start_time) + r.hours * 60 && mins(r.start_time) < mins(o.start_time) + o.hours * 60);

export const sessionLabel = (r) => `${r.start_time}–${endTime(r)} · ${r.name}`;

export const StudioCalendar = ({ rows }) => {
  const firstUpcoming = rows.filter((r) => r.status !== "declined").map((r) => r.date).sort().find((d) => d >= new Date().toISOString().slice(0, 10));
  const [month, setMonth] = useState(firstUpcoming ? parseISO(firstUpcoming) : new Date());
  const days = eachDayOfInterval({ start: startOfWeek(startOfMonth(month)), end: endOfWeek(endOfMonth(month)) });
  const byDate = rows.filter((r) => r.status === "approved" || r.status === "pending")
    .reduce((m, r) => ({ ...m, [r.date]: [...(m[r.date] || []), r].sort((a, b) => mins(a.start_time) - mins(b.start_time)) }), {});
  const jump = (id) => document.getElementById(`studio-row-${id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });

  return (
    <div className="card-surface rounded-2xl p-4 sm:p-6 mb-8" data-testid="studio-calendar">
      <div className="flex items-center justify-between mb-5">
        <h3 data-testid="studio-calendar-month" className="font-cond uppercase font-bold tracking-wider text-2xl">Studio calendar · {format(month, "MMMM yyyy")}</h3>
        <div className="flex gap-2">
          <button data-testid="studio-calendar-prev" onClick={() => setMonth(addMonths(month, -1))} className="btn-line rounded-full w-9 h-9 flex items-center justify-center" aria-label="Previous month"><ChevronLeft className="w-4 h-4" /></button>
          <button data-testid="studio-calendar-next" onClick={() => setMonth(addMonths(month, 1))} className="btn-line rounded-full w-9 h-9 flex items-center justify-center" aria-label="Next month"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 sm:gap-2 text-center font-cond uppercase tracking-widest text-xs text-slate-500 mb-2">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1 sm:gap-2">
        {days.map((day) => {
          const key = format(day, "yyyy-MM-dd");
          const list = byDate[key] || [];
          return (
            <div key={key} data-testid={`studio-day-${key}`}
              className={`min-h-[80px] rounded-lg border p-1.5 text-left ${isSameMonth(day, month) ? "border-[#222634] bg-[#0d0f14]" : "border-transparent opacity-30"}`}>
              <div className={`font-mono-ui text-xs ${isToday(day) ? "text-lime font-bold" : "text-slate-400"}`}>{format(day, "d")}</div>
              <div className="mt-1 space-y-1">
                {list.map((r) => {
                  const clash = studioClashes(r, rows).length > 0;
                  return (
                    <button key={r.id} onClick={() => jump(r.id)} data-testid={`studio-chip-${r.id}`} title={sessionLabel(r)}
                      className={`w-full text-left rounded px-1.5 py-1 text-[10px] sm:text-[11px] font-bold leading-tight truncate flex items-center gap-1 ${r.status === "approved" ? "bg-lime text-black" : "border border-amber-300/60 text-amber-200"} ${clash ? "ring-2 ring-red-500" : ""}`}>
                      {clash && <AlertTriangle className="w-3 h-3 shrink-0 text-red-500" />}
                      <span className="truncate">{r.start_time} {r.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-6 mt-4 text-xs text-slate-400">
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-lime" /> Approved</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm border border-amber-300/60" /> Pending</span>
        <span className="flex items-center gap-2"><AlertTriangle className="w-3 h-3 text-red-500" /> Overlaps an approved session</span>
      </div>
    </div>
  );
};
